<template>
  <div class="page-wrapper">
    <LoadingSpinner v-if="isProcessing" text="Procesando solicitud..." />

    <div class="container-fluid px-0 py-0">
      <div class="main-card shadow-sm">
        <div
          class="card-header-custom border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <h1 class="h4 mb-0 fw-bold text-dark">Solicitudes de Acceso</h1>
            <span class="badge bg-light text-dark border shadow-sm">
              <i class="fas fa-inbox text-primary me-1"></i>
              Total: {{ registrations.length }}
            </span>
          </div>
        </div>

        <div class="p-4 bg-light border-bottom">
          <div class="row">
            <div class="col-md-5">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  v-model="searchTerm"
                  class="form-control border-start-0 ps-0"
                  placeholder="Buscar por nombre, DNI o email..."
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div
            v-if="!loading && registrations.length === 0"
            class="text-center py-5"
          >
            <div class="text-secondary mb-3">
              <i class="fas fa-folder-open fa-3x opacity-50"></i>
            </div>
            <p class="text-muted">No hay solicitudes pendientes.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="bg-light text-secondary text-uppercase small">
                <tr>
                  <th class="ps-4 py-1 border-0">Solicitante</th>
                  <th class="py-1 border-0">Contacto</th>
                  <th class="py-1 border-0">Concesionaria Solicitada</th>
                  <th class="pe-4 py-1 border-0 text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <TableSpinner
                  v-if="loading"
                  :colspan="4"
                  text="Cargando solicitudes..."
                />

                <template v-else>
                  <tr
                    v-for="registration in registrations"
                    :key="registration.id"
                  >
                    <td class="ps-4 py-1">
                      <div class="d-flex align-items-center">
                        <div
                          class="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center mr-3"
                          style="
                            width: 40px;
                            height: 40px;
                            background-color: #e7f1ff;
                            color: #0d6efd;
                            font-weight: bold;
                          "
                        >
                          {{
                            registration.name
                              ? registration.name.charAt(0).toUpperCase()
                              : "S"
                          }}
                        </div>
                        <div class="d-flex flex-column">
                          <span class="fw-bold text-dark"
                            >{{ registration.lastname }},
                            {{ registration.name }}</span
                          >
                          <small class="text-muted text-monospace">
                            <i class="fas fa-id-card mr-1"></i>
                            {{ registration.dni }}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td class="py-1">
                      <div class="d-flex flex-column small">
                        <span class="text-dark"
                          ><i class="fas fa-envelope text-muted me-2"></i>
                          {{ registration.email }}</span
                        >
                      </div>
                    </td>

                    <td class="py-1">
                      <div class="d-flex align-items-center">
                        <i class="fas fa-building text-secondary me-2"></i>
                        <span
                          class="d-inline-block text-truncate fw-medium text-dark"
                          style="max-width: 250px"
                          :title="registration.concessionaire.business_name"
                        >
                          {{ registration.concessionaire.business_name }}
                        </span>
                      </div>
                    </td>

                    <td class="pe-4 py-1 text-end">
                      <ActionDropdown>
                        <button
                          class="dropdown-item d-flex align-items-center"
                          @click="viewMore(registration)"
                        >
                          <i class="fas fa-eye text-info fa-fw me-2"></i> Ver
                          detalles
                        </button>

                        <div class="dropdown-divider"></div>

                        <button
                          class="dropdown-item d-flex align-items-center text-success"
                          @click="confirmApprove(registration.id)"
                        >
                          <i class="fas fa-check-circle fa-fw me-2"></i> Aprobar
                        </button>

                        <button
                          class="dropdown-item d-flex align-items-center text-danger"
                          @click="confirmReject(registration.id)"
                        >
                          <i class="fas fa-times-circle fa-fw me-2"></i>
                          Rechazar
                        </button>
                      </ActionDropdown>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div
          class="card-footer-custom p-3 bg-light border-top d-flex justify-content-end"
          v-if="lastPage > 1"
        >
          <div class="btn-group shadow-sm">
            <button
              @click="fetchRegistros(1)"
              class="btn btn-white border"
              :disabled="currentPage === 1"
            >
              Primera
            </button>
            <button
              @click="fetchRegistros(currentPage - 1)"
              class="btn btn-white border"
              :disabled="currentPage === 1"
            >
              Anterior
            </button>
            <button
              @click="fetchRegistros(currentPage + 1)"
              class="btn btn-white border"
              :disabled="!hasMorePages"
            >
              Siguiente
            </button>
            <button
              @click="fetchRegistros(lastPage)"
              class="btn btn-white border"
              :disabled="currentPage === lastPage"
            >
              Última
            </button>
          </div>
        </div>
      </div>
    </div>

    <RegistrationModal
      v-if="isRegistrationModalOpen"
      :isOpen="isRegistrationModalOpen"
      :registration="selectedRegistration"
      @close="isRegistrationModalOpen = false"
      @open-positions-modal="openPositionsModal"
      @approve="confirmApprove"
      @reject="confirmReject"
    />

    <PositionsModal
      v-if="isPositionsModalOpen"
      :isOpen="isPositionsModalOpen"
      :positions="selectedPositions"
      @close="closePositionsModal"
    />
  </div>
</template>

<script>
import { ref, watch, onMounted } from "vue";
import RegistrationModal from "./modals/RegistrationModal.vue";
import PositionsModal from "./modals/PositionsModal.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ActionDropdown from "@/components/common/ActionDropdown.vue";

import TableSpinner from "@/components/ui/TableSpinner.vue";
import { debounce } from "lodash";

import {
  fetchRegistrations,
  approveRegistration,
  rejectRegistration,
} from "@/services/api/registration.js";
import Swal from "sweetalert2";

export default {
  components: {
    RegistrationModal,
    PositionsModal,
    LoadingSpinner,
    ActionDropdown,
    TableSpinner,
  },
  setup() {
    const isProcessing = ref(false);
    const registrations = ref([]);
    const isRegistrationModalOpen = ref(false);
    const selectedRegistration = ref(null);
    const isPositionsModalOpen = ref(false);
    const selectedPositions = ref([]);

    const loading = ref(false);
    const searchTerm = ref("");
    const orderByMethod = ref("asc");
    const perPage = ref(10);
    const status = ref(0);
    const currentPage = ref(1);
    const lastPage = ref(1);
    const hasMorePages = ref(false);

    const fetchRegistros = async (page = 1) => {
      loading.value = true;
      try {
        const data = await fetchRegistrations({
          search: searchTerm.value,
          orderByMethod: orderByMethod.value,
          perPage: perPage.value,
          status: status.value,
          page,
        });
        registrations.value = data.data || [];
        currentPage.value = data.meta.current_page;
        lastPage.value = data.meta.last_page;
        hasMorePages.value = data.meta.current_page < data.meta.last_page;
      } catch (error) {
        console.error("Error fetching registrations:", error);
      } finally {
        loading.value = false;
      }
    };

    const handleModalSuccess = () => {
      fetchRegistros(currentPage.value);
    };

    const viewMore = (registration) => {
      selectedRegistration.value = registration;
      isRegistrationModalOpen.value = true;
    };

    const openPositionsModal = (positions) => {
      selectedPositions.value = positions;
      isPositionsModalOpen.value = true;
    };

    const closePositionsModal = () => {
      isPositionsModalOpen.value = false;
    };

    const confirmApprove = async (registrationId) => {
      if (isProcessing.value) return;

      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción aprobará el registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, aprobar",
      });

      if (result.isConfirmed) {
        isProcessing.value = true;
        try {
          await approveRegistration(registrationId);
          await fetchRegistros(currentPage.value);
          isRegistrationModalOpen.value = false;

          Swal.fire("¡Aprobado!", "El registro ha sido aprobado.", "success");
        } catch (error) {
          Swal.fire("Error", "No se pudo aprobar el registro.", "error");
          console.error("Error approving registration:", error);
        } finally {
          isProcessing.value = false;
        }
      }
    };

    const confirmReject = async (registrationId) => {
      if (isProcessing.value) return;

      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción rechazará el registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sí, rechazar",
      });

      if (result.isConfirmed) {
        isProcessing.value = true;
        try {
          await rejectRegistration(registrationId);
          await fetchRegistros(currentPage.value);
          isRegistrationModalOpen.value = false;

          Swal.fire("¡Rechazado!", "El registro ha sido rechazado.", "success");
        } catch (error) {
          Swal.fire("Error", "No se pudo rechazar el registro.", "error");
          console.error("Error rejecting registration:", error);
        } finally {
          isProcessing.value = false;
        }
      }
    };

    const debouncedSearch = debounce(() => {
      fetchRegistros(1);
    }, 500);

    watch(searchTerm, () => {
      debouncedSearch();
    });

    watch([orderByMethod, perPage, status], () => {
      fetchRegistros(1);
    });

    onMounted(() => {
      fetchRegistros();
    });

    return {
      isProcessing,
      confirmApprove,
      confirmReject,
      registrations,
      isRegistrationModalOpen,
      selectedRegistration,
      isPositionsModalOpen,
      selectedPositions,
      loading,
      searchTerm,
      fetchRegistros,
      handleModalSuccess,
      viewMore,
      openPositionsModal,
      closePositionsModal,
      currentPage,
      lastPage,
      hasMorePages,
      orderByMethod,
      perPage,
      status,
    };
  },
};
</script>

<style scoped>
.page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.main-card {
  background: white;
  border-radius: 0.75rem;
  /* 12px */
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header-custom {
  padding: 1.5rem 2rem;
  background-color: white;
}

.card-footer-custom {
  background-color: #f8f9fa;
}

.input-group-text {
  background-color: white;
  border-color: #ced4da;
}

.form-control {
  border-color: #ced4da;
  font-size: 0.95rem;
  padding: 0.6rem 0.75rem;
}

.form-control:focus {
  box-shadow: none;
  border-color: #86b7fe;
  background-color: #fbfdff;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.text-monospace {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}

.bg-primary-subtle {
  background-color: #cfe2ff !important;
  color: #084298 !important;
}

.fa-fw {
  width: 1.25em;
  text-align: center;
}

.btn-white {
  background-color: white;
  color: #495057;
}

.btn-white:hover {
  background-color: #e9ecef;
}

.table-responsive::-webkit-scrollbar {
  height: 8px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .card-header-custom {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
